import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { I18n } from '@ngx-translate/i18n-polyfill';
import {
    Color,
    DateFormat,
    forLifeOf,
    getRouterLinkUrlById,
    LifecycleAware,
    StatusColor
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import {
    GetSupplierBasicInformationRequest,
    GetSupplierProgramInformationResponse
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/supplier-details/get-supplier-program-information';
import { GetSupplierProgramInformationService } from '@lsq-frontend/remote-services/financing-graphql';
import {
    atlas,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_SUPPLIERS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { SupplierStatusDisplay } from '../../../supply-chain-direct.models';

@Component({
    selector: 'supplier-program-information',
    template: `
        <card [headerTitle]="headerTitle">
            <div card-body [style.minHeight]="'33rem'">
                <lsq-circle-loader *ngIf="!programInformation"></lsq-circle-loader>
                <div *ngIf="!!programInformation" [style.padding]="'3rem 4rem 0 4rem'">
                    <div class="row">
                        <div class="col-sm-12 col-lg-6" [style.marginBottom]="'3rem'">
                            <p class="data-label" [style.marginBottom]="'1rem'">
                                <span id="status-label" i18n>Status</span>
                            </p>
                            <status-pill
                                id="status"
                                *ngIf="programInformation.status"
                                [status]="programInformation.status"
                                [color]="statusColor[programInformation.status]"
                                [displayValueMap]="supplierStatusDisplay"
                                [width]="'10rem'"
                            >
                            </status-pill>
                        </div>
                        <div class="col-sm-12 col-lg-6" [style.marginBottom]="'3rem'">
                            <p class="data-label">
                                <span id="enrollment-date-label" i18n>Enrollment date</span>
                            </p>
                            <div [style.paddingTop]="'0.5rem'" id="enrollment-date">
                                <div
                                    class="p body truncate"
                                    [style.paddingTop]="'0.5rem'"
                                    id="enrollment-date"
                                >
                                    {{
                                        !programInformation.enrollmentDate
                                            ? '—'
                                            : (programInformation.enrollmentDate
                                              | date : dateFormat.LsqShort)
                                    }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-12 col-lg-6" [style.marginBottom]="'3rem'">
                            <p class="data-label">
                                <span id="open-invoices-label" i18n>Open invoices</span>
                            </p>
                            <div
                                class="p body truncate"
                                [style.paddingTop]="'0.5rem'"
                                id="open-invoices"
                            >
                                {{
                                    !programInformation.totalOpenInvoices
                                        ? '—'
                                        : programInformation.totalOpenInvoices
                                }}
                            </div>
                        </div>
                        <div class="col-sm-12 col-lg-6" [style.marginBottom]="'3rem'">
                            <p class="data-label">
                                <span id="avg-paid-early-label" i18n>avg. days paid early</span>
                            </p>
                            <div
                                class="p body truncate"
                                [style.paddingTop]="'0.5rem'"
                                id="avg-paid-early"
                            >
                                {{
                                    !programInformation.earlyPaidAvgDays
                                        ? '—'
                                        : (programInformation.earlyPaidAvgDays | days)
                                }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-12 col-lg-6" [style.marginBottom]="'3rem'">
                            <p class="data-label">
                                <span id="percent-used-label" i18n>% used</span>
                            </p>
                            <div
                                class="p body truncate"
                                [style.paddingTop]="'0.5rem'"
                                id="percent-used"
                            >
                                {{
                                    !programInformation.percentUsed
                                        ? '—'
                                        : programInformation.percentUsed + '%'
                                }}
                            </div>
                        </div>
                        <div
                            class="col-sm-12 col-lg-6"
                            [style.marginBottom]="'3rem'"
                            *ngIf="programInformation.buyerPaidEarlyPaymentFeePercentageVisible"
                        >
                            <p class="data-label">
                                <span id="fee-share-label" i18n>Fee Share</span>
                                <lsq-icon-popover
                                    [text]="feeShareToolTipText"
                                    [height]="14"
                                    [width]="14"
                                    [icon]="icons.QuestionCircleLight"
                                    [isDark]="true"
                                    class="fee-share-tooltip-icon"
                                >
                                </lsq-icon-popover>
                            </p>
                            <div
                                class="p body truncate"
                                [style.paddingTop]="'0.5rem'"
                                id="fee-share"
                            >
                                {{
                                    (programInformation.buyerPaidEarlyPaymentFeePercentage || 0) +
                                        '%'
                                }}
                            </div>
                        </div>
                    </div>
                    <div
                        class="row"
                        *ngIf="programInformation.buyerPaidEarlyPaymentFeePercentageVisible"
                    >
                        <div class="col-sm-12 col-lg-6" [style.marginBottom]="'3rem'">
                            <p class="data-label">
                                <span id="total-paid-fees-label" i18n>Total Paid Fees</span>
                            </p>
                            <div
                                class="p body truncate"
                                [style.paddingTop]="'0.5rem'"
                                id="total-paid-fees"
                            >
                                {{ programInformation.totalPaidFees || 0 | lsqCurrency }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card>
    `,
    styleUrls: ['./supplier-basic-information.component.scss']
})
@LifecycleAware()
export class SupplierProgramInformationComponent implements OnInit {
    public headerTitle = this._i18n('Program Information');
    public programInformation!: GetSupplierProgramInformationResponse;
    public readonly statusColor = StatusColor as object as Record<any, Color>;
    public dateFormat = DateFormat;
    public readonly icons = Icons;

    public readonly feeShareToolTipText = this._i18n({
        value: 'The displayed shared fee rate is the percentage you are paying towards your supplier’s early payment fees.'
    });

    constructor(
        private _i18n: I18n,
        private _activatedRoute: ActivatedRoute,
        private _getSupplierProgramInformationService: GetSupplierProgramInformationService,
        public supplierStatusDisplay: SupplierStatusDisplay
    ) {}

    ngOnInit(): void {
        this._activatedRoute.params.pipe(forLifeOf(this)).subscribe(async (params) => {
            try {
                this.programInformation =
                    await this._getSupplierProgramInformationService.executeOnce(
                        new GetSupplierBasicInformationRequest({
                            supplierKey: params?.sellerKey
                        })
                    );
            } catch (e) {
                getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_ACCOUNT_SUPPLIERS);
            }
        });
    }
}
